import { flowRight, get } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { EditorState, Modifier, convertToRaw } from 'draft-js';
import Editor, { composeDecorators } from 'draft-js-plugins-editor';
import createDndPlugin from 'draft-js-drag-n-drop-plugin';
import createFocusPlugin from 'draft-js-focus-plugin';
import { connect } from 'react-redux';
import classNames from 'classnames';
import decorateComponentWithProps from 'decorate-component-with-props';
import { EXPERIMENT_CODE_BLOCK } from '../../constants/experiments';
import { APP_TEXT_COLOR_PATH } from '../../constants/wix-params';
import { openModal } from '../../actions/open-modal';
import { insertTextEditorEntity } from '../../actions/insert-text-editor-entity';
import { updateTextEditor } from '../../actions/update-text-editor';
import { inlineToolbarShown } from '../../actions/inline-toolbar-shown';
import { upload, TYPE_IMAGE } from '../../actions/upload';
import { setTextEditorEntityData } from '../../actions/set-text-editor-entity-data';
import { updateTextEditorVideoEmbed } from '../../actions/update-text-editor-video-embed';
import { uploadStarted } from '../../actions/upload-started';
import { uploadEnded } from '../../actions/upload-ended';
import TextEditorImage from './text-editor-image';
import TextEditorHashtag from './text-editor-hashtag';
import TextEditorVideoEmbed from './text-editor-video-embed';
import TextEditorInlineToolbar from './text-editor-inline-toolbar';
import withDeviceType from '../../hoc/with-device-type';
import withSettingsColor from '../../hoc/with-settings-color';
import withTranslate from '../../hoc/with-translate';
import createImagePlugin from './plugins/image';
import createVideoEmbedPlugin from './plugins/video-embed';
import createMediaBlockPlugin from './plugins/media-block';
import createCodeBlockPlugin from './plugins/code-block';
import createHashtagPlugin from './plugins/hashtag';
import { getTextEditorStateByKey } from '../../selectors/text-editor-selectors';
import { getIsMac } from '../../selectors/user-agent-selectors';
import {
  hasLinksInSelection,
  createEditorState,
  getFocusableContainerId,
  isAtomicBlockFocused,
} from './modifiers/text-editor-utils';
import { getSelectionRange } from './utils/text-selection-utils';
import withExperiment from '../../hoc/with-experiment';
import getToolbarPosition from './utils/get-toolbar-position';
import resizeImage from '../../services/resize-image';
import createTextEditorDecorator from './decorators/create-text-editor-decorator';
import createBlockRenderMap from './utils/create-block-render-map';
import { handleKeyCommand, keyBindingFn, keyBindingsLabel } from './utils/key-bindings';

export class TextEditor extends Component {
  constructor(props) {
    super(props);

    const mediaBlockPlugin = createMediaBlockPlugin({
      onUpload: this.handleImageChange,
      onFileSelect: this.handleFileSelect,
      onVideoEmbedChange: this.handleVideoEmbedChange,
      isDesktop: props.isDesktop,
      getEditorRef: () => this.editor,
      theme: get(props.theme, 'mediaBlock'),
      ...get(props.config, 'mediaBlock'),
    });
    this.MediaBlockEditor = mediaBlockPlugin.MediaBlockEditor;

    const dndPlugin = createDndPlugin();
    const focusPlugin = createFocusPlugin({
      theme: get(props.theme, 'focus'),
    });

    const imagePlugin = createImagePlugin({
      Image: TextEditorImage,
      decorator: composeDecorators(focusPlugin.decorator, mediaBlockPlugin.decorator, dndPlugin.decorator),
    });

    const videoEmbedPlugin = createVideoEmbedPlugin({
      VideoEmbed: TextEditorVideoEmbed,
      decorator: composeDecorators(focusPlugin.decorator, mediaBlockPlugin.decorator, dndPlugin.decorator),
    });

    this.plugins = [focusPlugin, mediaBlockPlugin, dndPlugin, imagePlugin, videoEmbedPlugin];

    this.blockRenderMap = createBlockRenderMap({ titleTheme: get(props.theme, 'title') });

    if (props.isCodeBlockEnabled) {
      this.plugins.push(
        createCodeBlockPlugin({
          backgroundColor: props.codeBackgroundColor,
        })
      );
    }

    if (props.enableHashtags) {
      this.plugins.push(
        createHashtagPlugin({
          Hashtag: decorateComponentWithProps(TextEditorHashtag, {
            theme: get(props.theme, 'hashtag'),
          }),
        })
      );
    }

    this.decorators = [createTextEditorDecorator({ theme: props.theme })];

    this.state = {
      editorState: createEditorState(this.props.initialValue),
      inlineToolbar: {
        isVisible: false,
      },
    };
  }

  componentDidMount = () => {
    this.props.autoFocus && this.editor.focus();
  };

  componentWillReceiveProps = ({ editorState, initialValue }) => {
    if (editorState && editorState !== this.props.editorState) {
      this.handleEditorChange(editorState);
    }

    if (this.props.initialValue !== initialValue && initialValue) {
      const decorator = this.state.editorState.getDecorator();
      this.handleEditorChange(createEditorState(initialValue, decorator));
    }
  };

  componentDidUpdate = (prevProps, { editorState, inlineToolbar }) => {
    const hasFocus = this.state.editorState && this.state.editorState.getSelection().getHasFocus();
    const hadFocus = editorState.getSelection().getHasFocus();
    if (hasFocus && !hadFocus) {
      this.editor.focus();
    }
    if (!inlineToolbar.isVisible && this.state.inlineToolbar.isVisible) {
      this.props.inlineToolbarShown(this.state.inlineToolbar.isVisible);
    }
  };

  setEditor = ref => (this.editor = ref);
  setContainer = ref => (this.container = ref);
  setInlineToolbar = ref => (this.inlineToolbar = ref);

  getEditableElement = () => (this.editor ? findDOMNode(this.editor).querySelector('[contenteditable]') : null);

  handleFileSelect = imageData => {
    const { insertTextEditorEntity, editorState, identifier } = this.props;
    const props = { src: imageData };
    const entityKey = editorState
      .getCurrentContent()
      .createEntity('IMAGE', 'IMMUTABLE', props)
      .getLastCreatedEntityKey();

    insertTextEditorEntity({
      entityKey,
      identifier,
    });
  };

  handleImageChange = ({ file, entityKey }) => {
    this.props.uploadStarted({ type: TYPE_IMAGE });
    return resizeImage(file).then(imageData => {
      const { setTextEditorEntityData, upload, identifier, uploadEnded } = this.props;
      setTextEditorEntityData({
        entityData: {
          src: imageData,
          isLoading: true,
        },
        entityKey,
        identifier,
      });

      const shouldUploadDataUrl = file.type !== 'image/gif';
      upload(shouldUploadDataUrl ? imageData.dataUrl : file, TYPE_IMAGE, file => {
        uploadEnded({ type: TYPE_IMAGE });
        return setTextEditorEntityData({
          entityData: {
            src: file,
            isLoading: false,
          },
          entityKey,
          identifier,
        });
      });
    });
  };

  handleVideoEmbedChange = data =>
    this.props.updateTextEditorVideoEmbed({
      ...data,
      identifier: this.props.identifier,
    });

  handleBackgroundMouseDown = () => (this.shouldFocusContent = document.activeElement !== this.getEditableElement());

  handleBackgroundClick = event => {
    if (this.shouldFocusContent) {
      this.editor.focus(); //tODO;
      return;
    }

    const editableElement = this.getEditableElement();
    let currentElement = event.target;
    let shouldBlur = true;

    while (currentElement) {
      if (currentElement === editableElement || currentElement === this.inlineToolbar) {
        shouldBlur = false;
        break;
      }
      currentElement = currentElement.parentNode;
    }

    if (shouldBlur) {
      this.editor.blur();
    }
  };

  handleReturn = () => {
    // prevents editor from crashing when enter is pressed while atomic block is focused
    if (isAtomicBlockFocused(this.state.editorState)) {
      return 'handled';
    }
    return 'not-handled';
  };

  handleEditorChange = editorState => {
    const { onChange, updateTextEditor, identifier, isDesktop } = this.props;

    this.setState({ editorState });
    updateTextEditor({ identifier, editorState });
    if (onChange) {
      onChange(convertToRaw(editorState.getCurrentContent()));
    }

    const selection = editorState.getSelection();
    if (!selection.isCollapsed() && selection.getHasFocus() && isDesktop) {
      setTimeout(() => {
        // setTimeout is used here to get correct selection position
        const selectionRange = getSelectionRange(window.getSelection());
        if (!selectionRange) {
          this.hideInlineToolbar();
          return;
        }

        this.showInlineToolbar(selectionRange);
      });
    } else {
      this.hideInlineToolbar();
    }
  };

  showInlineToolbar = selectionRange => {
    this.setState({
      inlineToolbar: {
        isVisible: true,
        position: getToolbarPosition(
          this.container.getBoundingClientRect(),
          selectionRange.getBoundingClientRect(),
          this.inlineToolbar.children[0].getBoundingClientRect()
        ),
      },
    });
  };

  hideInlineToolbar = () => {
    this.setState({
      inlineToolbar: {
        isVisible: false,
      },
    });
  };

  onKeyCommand = command => {
    const { editorState, openModal, identifier } = this.props;
    return handleKeyCommand({
      command,
      editorState,
      identifier,
      handleEditorChange: this.handleEditorChange,
      openModal,
    });
  };

  onBeforeInput = chars => {
    const { editorState } = this.props;
    const selection = editorState.getSelection();

    // prevents editor from crashing when space is pressed while atomic block is focused
    if (isAtomicBlockFocused(editorState) && chars === ' ') {
      return 'handled';
    }

    if (selection.isCollapsed() && !hasLinksInSelection(editorState)) {
      const content = editorState.getCurrentContent();
      const inlineStyle = editorState.getCurrentInlineStyle();
      const newContent = Modifier.insertText(content, selection, chars, inlineStyle);
      const newState = EditorState.push(editorState, newContent, 'insert-characters');
      this.handleEditorChange(newState);
      return 'handled';
    }

    return 'not-handled';
  };

  renderInlineToolbar = () => {
    const { inlineToolbar } = this.state;
    const { identifier, theme } = this.props;

    return (
      <div ref={this.setInlineToolbar} style={{ display: inlineToolbar.isVisible ? 'block' : 'none' }}>
        <TextEditorInlineToolbar
          position={inlineToolbar.position}
          identifier={identifier}
          theme={get(theme, 'toolbar')}
        />
      </div>
    );
  };

  render = () => {
    const { editorState } = this.state;
    const { identifier, isCodeBlockEnabled, isMac, placeholder, type, theme = {}, t } = this.props;
    const firstBlock = editorState
      .getCurrentContent()
      .getBlockMap()
      .first()
      .toJSON();
    const firstBlockType = firstBlock.type;
    const firstBlockTextAlignment = firstBlock.data.textAlignment;
    const className = classNames(
      'text-editor',
      get(theme, 'container'),
      get(theme, `types.${type}`),
      get(theme, `placeholder.${firstBlockTextAlignment}`),
      {
        [get(theme, 'placeholder.hidden')]: firstBlockType !== 'unstyled',
      }
    );
    const { MediaBlockEditor } = this;

    return (
      <div
        className={className}
        ref={this.setContainer}
        id={getFocusableContainerId(identifier)}
        role="form"
        aria-label={keyBindingsLabel({ isCodeBlockEnabled, isMac, t })}
        tabIndex="0"
      >
        {this.renderInlineToolbar()}
        <Editor
          ref={this.setEditor}
          editorState={editorState}
          onChange={this.handleEditorChange}
          onFocus={this.props.onFocus}
          onBlur={this.props.onBlur}
          handleReturn={this.handleReturn} //eslint-disable-line react/jsx-handler-names
          handleKeyCommand={this.onKeyCommand}
          keyBindingFn={keyBindingFn(isCodeBlockEnabled)}
          handleBeforeInput={this.onBeforeInput}
          blockStyleFn={theme.blockStyleFn}
          blockRenderMap={this.blockRenderMap}
          placeholder={placeholder}
          plugins={this.plugins}
          decorators={this.decorators}
          spellCheck
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
        />
        <MediaBlockEditor theme={get(theme, 'toolbar')}/>
      </div>
    );
  };
}

TextEditor.propTypes = {
  autoFocus: PropTypes.bool,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  editorState: PropTypes.object,
  updateTextEditor: PropTypes.func,
  upload: PropTypes.func,
  uploadStarted: PropTypes.func,
  uploadEnded: PropTypes.func,
  setTextEditorEntityData: PropTypes.func,
  updateTextEditorVideoEmbed: PropTypes.func,
  TextEditorToolbar: PropTypes.node,
  isDesktop: PropTypes.bool,
  isMac: PropTypes.bool,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  inlineToolbarShown: PropTypes.func,
  initialValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  identifier: PropTypes.string.isRequired,
  isCodeBlockEnabled: PropTypes.bool,
  enableHashtags: PropTypes.bool,
  codeBackgroundColor: PropTypes.string,
  theme: PropTypes.object,
  config: PropTypes.object,
  insertTextEditorEntity: PropTypes.func,
  t: PropTypes.func.isRequired,
  openModal: PropTypes.func,
};

const mapStateToProps = (state, ownProps) => ({
  editorState: getTextEditorStateByKey(state, ownProps.identifier),
  isMac: getIsMac(state),
});

const mapDispatchToProps = {
  openModal,
  updateTextEditor,
  inlineToolbarShown,
  upload,
  uploadStarted,
  uploadEnded,
  setTextEditorEntityData,
  updateTextEditorVideoEmbed,
  insertTextEditorEntity,
};

export default flowRight(
  connect(mapStateToProps, mapDispatchToProps),
  withExperiment({
    isCodeBlockEnabled: EXPERIMENT_CODE_BLOCK,
  }),
  withDeviceType,
  withSettingsColor(APP_TEXT_COLOR_PATH, 'codeBackgroundColor', 'color-5', 0.1),
  withTranslate
)(TextEditor);



// WEBPACK FOOTER //
// ./app/components/text-editor/text-editor.js